import { Link, useRouterState } from "@tanstack/react-router";
import { Compass, Flame, Sparkles, Map, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Tab {
  to: string;
  label: string;
  icon: LucideIcon;
  match: (pathname: string) => boolean;
}

// Five tabs, per project spec: First Gate · Explore · Practice · Journey · Profile.
const TABS: Tab[] = [
  {
    to: "/gates/1",
    label: "First Gate",
    icon: Flame,
    match: (p) => p === "/gates/1" || p.startsWith("/gates"),
  },
  {
    to: "/constellation",
    label: "Explore",
    icon: Compass,
    match: (p) =>
      p === "/" ||
      p.startsWith("/constellation") ||
      p.startsWith("/music") ||
      p.startsWith("/wisdom") ||
      p.startsWith("/books") ||
      p.startsWith("/library") ||
      p.startsWith("/search"),
  },
  {
    to: "/activations",
    label: "Practice",
    icon: Sparkles,
    match: (p) => p.startsWith("/activations") || p.startsWith("/method"),
  },
  {
    to: "/journey",
    label: "Journey",
    icon: Map,
    match: (p) => p.startsWith("/journey"),
  },
  {
    to: "/me",
    label: "Profile",
    icon: User,
    match: (p) => p === "/me" || p.startsWith("/me/") || p.startsWith("/account") || p.startsWith("/auth"),
  },
];

/**
 * Persistent bottom navigation for mobile viewports.
 * Hidden on lg+ screens where the top nav takes over.
 * Respects iOS safe-area, avoids covering the mini-player (mini-player uses bottom-0
 * with its own translate; we sit above it on mobile via bottom-[--mini-player-offset]).
 */
export function MobileTabBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Hide on reading/focus routes where a persistent nav would break immersion.
  const hidden =
    pathname.startsWith("/library/read") ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/lovable/");
  if (hidden) return null;

  return (
    <nav
      aria-label="Primary mobile navigation"
      className="fixed inset-x-0 bottom-0 z-40 border-t hairline bg-obsidian/95 backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="mx-auto grid max-w-md grid-cols-5">
        {TABS.map((tab) => {
          const active = tab.match(pathname);
          const Icon = tab.icon;
          return (
            <li key={tab.to}>
              <Link
                to={tab.to}
                aria-current={active ? "page" : undefined}
                className={`flex h-16 min-h-[44px] flex-col items-center justify-center gap-1 px-1 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                  active ? "text-gold-luminous" : "text-foreground/70 hover:text-gold"
                }`}
              >
                <Icon size={20} aria-hidden strokeWidth={active ? 2.25 : 1.75} />
                <span className="truncate">{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
